import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Instagram, Palette, Sparkles } from "lucide-react";

const COPY = {
  en: { eyebrow: "Extended Studio", title: "The collective", lead: "When a project asks for more hands, Kuryarti opens the studio to trusted collaborators — hand-picked for style continuity and pace." },
  ru: { eyebrow: "Расширенная студия", title: "Коллектив", lead: "Когда проект требует больше рук, Kuryarti подключает доверенных соавторов — подобранных по стилю и ритму." },
  es: { eyebrow: "Estudio Extendido", title: "El colectivo", lead: "Cuando un proyecto pide más manos, Kuryarti abre el estudio a colaboradoras de confianza — elegidas por continuidad de estilo y ritmo." },
};

export default function Collective() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/collaborators").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const pick = (obj, fallback = "") =>
    (obj && typeof obj === "object" && (obj[lang] || obj.en || obj.ru || obj.es)) || fallback;

  if (items.length === 0) return null;

  return (
    <section id="collective" className="relative bg-white py-24 lg:py-32 border-t border-[#0a0a0a] overflow-hidden">
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-3 flex items-center gap-2">
              <Sparkles size={12} className="text-[#00d4ff]" />
              · {c.eyebrow}
            </div>
            <h2 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-[#0a0a0a]">
              {c.title}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="font-sans text-base lg:text-lg text-[#4a4a4a] leading-relaxed max-w-2xl">{c.lead}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {items.map((p, i) => {
            const bio = pick(p.bio);
            return (
              <motion.article
                key={p.collab_id}
                data-testid={`collective-card-${p.collab_id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.12 }}
                className="group grid grid-cols-5 gap-5 items-start"
              >
                <div className="col-span-2">
                  <div
                    className="relative aspect-[3/4] overflow-hidden border border-[#0a0a0a] bg-[#f5f5f5]"
                    style={{ boxShadow: `5px 5px 0 0 ${p.accent || "#00d4ff"}` }}
                  >
                    {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />}
                    <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm px-3 py-1.5 border-t border-[#0a0a0a] font-mono text-[9px] uppercase tracking-[0.25em] text-[#0a0a0a] flex items-center justify-between">
                      <span>№ 0{i + 1}</span>
                      <span style={{ color: p.accent === "#0a0a0a" ? "#ff006e" : (p.accent || "#00d4ff") }}>online</span>
                    </div>
                  </div>
                </div>

                <div className="col-span-3 pt-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: p.accent === "#0a0a0a" ? "#ff006e" : (p.accent || "#00d4ff") }}>
                    {p.role}
                  </div>
                  <h3 className="mt-1 font-mono font-bold tracking-tight text-xl lg:text-2xl text-[#0a0a0a]">
                    {p.name}
                  </h3>
                  <div className="mt-1 font-mono text-xs text-[#8a8a8a] flex items-center gap-1.5">
                    <Instagram size={11} />
                    {p.handle}
                  </div>
                  <p className="mt-4 font-sans text-sm lg:text-[15px] text-[#4a4a4a] leading-relaxed">{bio}</p>
                  {(p.tags || []).length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-1.5">
                      {(p.tags || []).map((t) => (
                        <span key={t} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#4a4a4a] border border-[#e5e5e5] px-2 py-1">{t}</span>
                      ))}
                    </div>
                  )}
                  {p.tools && (
                    <div className="mt-5 pt-4 border-t border-dashed border-[#e5e5e5] flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a]">
                      <Palette size={11} className="text-[#0a0a0a]" />
                      {p.tools}
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
